/**
 * Form State S3 Service
 * 
 * Handles saving/loading form state to/from S3 via Lambda
 * Form states are stored as JSON files with reference number as filename
 * 
 * FIXED: Uses correct bucket (ef-event-shift-report-photostore) matching Lambda
 */

const API_URL = process.env.REACT_APP_SIGNATURE_API_URL || 
  'https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3';

// Configuration - FIXED bucket name to match Lambda
const FORM_STATE_CONFIG = {
  bucket: 'ef-event-shift-report-photostore', // FIXED: Match Lambda bucket
  prefix: 'form-states/',
  cacheDuration: 300000 // 5 minutes cache for list operations
};

// Cache for list operations
let listCache = {
  data: null,
  timestamp: null,
  username: null
};

/**
 * Helper: Create detailed error from API response
 */
const createDetailedError = async (response, defaultMessage) => {
  let data;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : { error: defaultMessage };
  } catch (e) {
    data = { error: defaultMessage };
  }
  
  const error = new Error(data.error || data.message || defaultMessage);
  error.statusCode = response.status;
  error.code = data.code;
  error.details = data.details;
  
  return error;
};

/**
 * Save form state to S3
 * @param {string} username - Username
 * @param {string} referenceNo - Reference number (used as filename)
 * @param {object} formState - Complete form state object
 * @returns {Promise<{success: boolean, s3Key: string, message: string}>}
 */
export const saveFormStateToS3 = async (username, referenceNo, formState) => {
  try {
    console.log('[FormStateService] Saving form state:', { username, referenceNo, hasData: !!formState });
    
    if (!API_URL) {
      throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    }

    if (!username || !referenceNo || !formState) {
      throw new Error('Username, reference number, and form state are required');
    }

    // Add metadata to form state
    const stateWithMetadata = {
      ...formState,
      savedAt: new Date().toISOString(),
      savedBy: username,
      referenceNo: referenceNo,
      version: '1.0'
    };

    // Attempt to merge with any existing saved state so autosaves build up a complete
    // JSON record across multiple partial saves. We do a deep merge where objects
    // are merged recursively and arrays prefer the new array when present (so
    // repeated structures are replaced by the most recent snapshot for that section).
    const deepMerge = (target, source) => {
      if (!source) return target;
      if (!target) return source;

      // If either is not an object, prefer source
      if (typeof target !== 'object' || typeof source !== 'object' || Array.isArray(target) || Array.isArray(source)) {
        // For arrays prefer source if it has length, otherwise keep target
        if (Array.isArray(source)) {
          return source.length > 0 ? source : target;
        }
        return source;
      }

      const out = { ...target };
      for (const key of Object.keys(source)) {
        if (source[key] === undefined) continue;
        if (Array.isArray(source[key])) {
          out[key] = source[key].length > 0 ? source[key] : (target[key] || []);
        } else if (source[key] && typeof source[key] === 'object') {
          out[key] = deepMerge(target[key] || {}, source[key]);
        } else {
          out[key] = source[key];
        }
      }
      return out;
    };

    // Try to load existing saved state and merge; do not fail the save if load fails.
    let mergedState = stateWithMetadata;
    try {
      const existing = await loadFormStateFromS3(username, referenceNo).catch(() => null);
      if (existing && existing.formState) {
        // Merge existing.formState with new stateWithMetadata
        mergedState = deepMerge(existing.formState, stateWithMetadata);
        // Ensure we update savedAt/updatedBy to the freshest values
        mergedState.savedAt = stateWithMetadata.savedAt;
        mergedState.savedBy = stateWithMetadata.savedBy;
        mergedState.referenceNo = referenceNo;
      }
    } catch (e) {
      // If merge attempt fails, continue with the new state only
      console.warn('[FormStateService] Could not load existing state to merge, continuing with new snapshot', e);
      mergedState = stateWithMetadata;
    }

    // Build a lightweight photo index to make it easy for the frontend to request
    // presigned URLs for images that were previously uploaded. This index is a
    // mapping of section -> array of photo metadata (s3Key, bucket, itemIndex, photoIndex)
    try {
      const photoIndex = {};
      const sections = ['ticketingItems','carparkItems','corporateRoomItems','cleaningMaintenanceItems','whsItems','otherOperationalItems','incidentItems','additionalTaskingItems','serviceComplaintItems','staffFeedbackItems'];
      sections.forEach(sec => {
        const items = mergedState[sec] || [];
        const secKey = sec.replace(/Items$/, '');
        const list = [];
        items.forEach((it, idx) => {
          if (it.photos && Array.isArray(it.photos)) {
            it.photos.forEach((p, pi) => {
              if (p && (p.s3Key || p.photoKey)) {
                list.push({
                  s3Key: p.s3Key || p.photoKey,
                  s3Url: p.s3Url || null,
                  bucket: p.bucket || FORM_STATE_CONFIG.bucket,
                  itemIndex: p.itemIndex != null ? p.itemIndex : idx + 1,
                  photoIndex: p.photoIndex != null ? p.photoIndex : pi + 1,
                  filename: p.filename || null,
                  uploadedAt: p.uploadedAt || null
                });
              }
            });
          }
        });
        if (list.length > 0) photoIndex[secKey] = list;
      });
      if (Object.keys(photoIndex).length > 0) {
        mergedState._photoIndex = photoIndex;
      }
    } catch (e) {
      console.warn('[FormStateService] Could not build photo index (non-fatal):', e && e.message);
    }

    console.log('[FormStateService] Form state size:', JSON.stringify(stateWithMetadata).length, 'bytes');

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'saveFormState',
        username: username,
        referenceNo: referenceNo,
        formState: mergedState
      })
    });

    if (!response.ok) {
      throw await createDetailedError(response, 'Failed to save form state');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to save form state');
    }

    // Invalidate cache after save
    listCache = { data: null, timestamp: null, username: null };

    console.log('[FormStateService] Form state saved successfully:', result.s3Key);

    return {
      success: true,
      s3Key: result.s3Key,
      message: result.message || 'Form state saved successfully'
    };
  } catch (error) {
    console.error('[FormStateService] Error saving form state to S3:', error);
    throw error; // Re-throw to let calling code handle it
  }
};

/**
 * List all saved form states for a user
 * @param {string} username - Username
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<{success: boolean, forms: Array, count: number}>}
 */
export const listFormStatesFromS3 = async (username, forceRefresh = false) => {
  try {
    console.log('[FormStateService] Listing form states for:', username, { forceRefresh });
    
    if (!API_URL) {
      throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    }

    if (!username) {
      throw new Error('Username is required');
    }

    // Check cache
    if (!forceRefresh && 
        listCache.data && 
        listCache.username === username &&
        Date.now() - listCache.timestamp < FORM_STATE_CONFIG.cacheDuration) {
      console.log('[FormStateService] Using cached list data');
      return {
        success: true,
        forms: listCache.data,
        count: listCache.data.length,
        fromCache: true
      };
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'listFormStates',
        username: username
      })
    });

    if (!response.ok) {
      throw await createDetailedError(response, 'Failed to list form states');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to list form states');
    }

    // Update cache
    listCache = {
      data: result.forms || [],
      timestamp: Date.now(),
      username: username
    };

    console.log('[FormStateService] Found', result.count || 0, 'form states');

    return {
      success: true,
      forms: result.forms || [],
      count: result.count || 0
    };
  } catch (error) {
    console.error('[FormStateService] Error listing form states from S3:', error);
    throw error; // Re-throw to let calling code handle it
  }
};

/**
 * Load a specific form state from S3
 * @param {string} username - Username
 * @param {string} referenceNo - Reference number
 * @returns {Promise<{success: boolean, formState: object}>}
 */
export const loadFormStateFromS3 = async (username, referenceNo) => {
  try {
    console.log('[FormStateService] Loading form state:', { username, referenceNo });
    
    if (!API_URL) {
      throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    }

    if (!username || !referenceNo) {
      throw new Error('Username and reference number are required');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'loadFormState',
        username: username,
        referenceNo: referenceNo
      })
    });

    if (!response.ok) {
      throw await createDetailedError(response, 'Failed to load form state');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to load form state');
    }

    console.log('[FormStateService] Form state loaded successfully');

    return {
      success: true,
      formState: result.formState
    };
  } catch (error) {
    console.error('[FormStateService] Error loading form state from S3:', error);
    throw error; // Re-throw to let calling code handle it
  }
};

/**
 * Delete a form state from S3
 * @param {string} username - Username
 * @param {string} referenceNo - Reference number
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteFormStateFromS3 = async (username, referenceNo) => {
  try {
    console.log('[FormStateService] Deleting form state:', { username, referenceNo });
    
    if (!API_URL) {
      throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    }

    if (!username || !referenceNo) {
      throw new Error('Username and reference number are required');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'deleteFormState',
        username: username,
        referenceNo: referenceNo
      })
    });

    if (!response.ok) {
      throw await createDetailedError(response, 'Failed to delete form state');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to delete form state');
    }

    // Invalidate cache after delete
    listCache = { data: null, timestamp: null, username: null };

    console.log('[FormStateService] Form state deleted successfully');

    return {
      success: true,
      message: result.message || 'Form state deleted successfully'
    };
  } catch (error) {
    console.error('[FormStateService] Error deleting form state from S3:', error);
    throw error; // Re-throw to let calling code handle it
  }
};

/**
 * Clear the list cache
 */
export const clearFormStateCache = () => {
  listCache = { data: null, timestamp: null, username: null };
  console.log('[FormStateService] Cache cleared');
};

/**
 * ADMIN: List all saved form states across users (requires admin privileges on backend)
 * @param {string} username - Admin username for authorization
 * @returns {Promise<{success: boolean, forms: Array}>}
 */
export const listAllFormStates = async (username) => {
  try {
    if (!API_URL) throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    if (!username) throw new Error('Username required for admin action');

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'listAllFormStates', username })
    });

    if (!response.ok) throw await createDetailedError(response, 'Failed to list all form states');

    const result = await response.json();
    if (!result.success) throw new Error(result.error || result.message || 'Failed to list all form states');

    return { success: true, forms: result.forms || [] };
  } catch (err) {
    console.error('[FormStateService] listAllFormStates error', err);
    throw err;
  }
};

/**
 * ADMIN: List active user sessions or statuses (backend must support this action)
 * @param {string} username - Admin username for authorization
 */
export const listActiveUsers = async (username) => {
  try {
    if (!API_URL) throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    if (!username) throw new Error('Username required for admin action');
    
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'listActiveUsers', username })
    });
    if (!response.ok) throw await createDetailedError(response, 'Failed to list active users');
    const result = await response.json();
    if (!result.success) throw new Error(result.error || result.message || 'Failed to list active users');
    return { success: true, users: result.users || [] };
  } catch (err) {
    console.error('[FormStateService] listActiveUsers error', err);
    throw err;
  }
};

/**
 * ADMIN: Load a specific user's saved form state (admin-level load)
 */
export const loadUserFormState = async (targetUsername, referenceNo) => {
  try {
    if (!API_URL) throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    if (!targetUsername || !referenceNo) throw new Error('targetUsername and referenceNo required');

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'adminLoadFormState', username: targetUsername, referenceNo })
    });

    if (!response.ok) throw await createDetailedError(response, 'Failed to load user form state');
    const result = await response.json();
    if (!result.success) throw new Error(result.error || result.message || 'Failed to load user form state');
    return { success: true, formState: result.formState };
  } catch (err) {
    console.error('[FormStateService] loadUserFormState error', err);
    throw err;
  }
};

/**
 * ADMIN: Restore a saved form state into a target user's saved slot (writes the JSON under their username/referenceNo)
 */
export const adminRestoreFormState = async (targetUsername, referenceNo, formState) => {
  try {
    if (!API_URL) throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    if (!targetUsername || !referenceNo || !formState) throw new Error('targetUsername, referenceNo and formState required');

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'adminRestoreFormState', targetUsername, referenceNo, formState })
    });

    if (!response.ok) throw await createDetailedError(response, 'Failed to restore form state for user');
    const result = await response.json();
    if (!result.success) throw new Error(result.error || result.message || 'Failed to restore form state');
    return { success: true, message: result.message || 'Restored' };
  } catch (err) {
    console.error('[FormStateService] adminRestoreFormState error', err);
    throw err;
  }
};

/**
 * ADMIN: Push a saved form state into a target user's live session (if supported by backend)
 * This is intended to notify the running frontend for that user (e.g., via a websocket or push mechanism)
 * Backend should attempt to deliver the provided formState to the user's active session. If not supported,
 * the backend MAY fall back to writing the formState to S3 under the user's referenceNo (same as restore).
 */
/**
 * ADMIN: Push a saved form state into a user's live session
 * @param {string} adminUsername - Admin username for authorization  
 * @param {string} targetUsername - Target user to push form to
 * @param {string} referenceNo - Form reference number
 * @param {object} formState - Form state to push
 */
export const adminPushToSession = async (adminUsername, targetUsername, referenceNo, formState) => {
  try {
    if (!API_URL) throw new Error('REACT_APP_SIGNATURE_API_URL not configured');
    if (!adminUsername) throw new Error('Admin username required for authorization');
    if (!targetUsername || !referenceNo || !formState) throw new Error('targetUsername, referenceNo and formState required');

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        action: 'adminPushToSession', 
        username: adminUsername,  // Admin username for auth
        targetUsername, 
        referenceNo, 
        formState 
      })
    });

    if (!response.ok) throw await createDetailedError(response, 'Failed to push form state to user session');
    const result = await response.json();
    if (!result.success) throw new Error(result.error || result.message || 'Failed to push form state to session');
    return { success: true, message: result.message || 'Pushed to session' };
  } catch (err) {
    console.error('[FormStateService] adminPushToSession error', err);
    throw err;
  }
};

/**
 * Check for pushed state from admin
 * Returns { hasPushed: boolean, formState: object|null }
 */
export const checkForPushedState = async (username) => {
  try {
    if (!username) throw new Error('Username required to check for pushed state');
    
    console.log('[FormStateService] Checking for pushed state for:', username);
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        action: 'checkPushedState',
        username 
      })
    });

    if (!response.ok) {
      // 404 means no pushed state exists - this is normal, don't throw
      if (response.status === 404) {
        return { hasPushed: false, formState: null };
      }
      throw await createDetailedError(response, 'Failed to check for pushed state');
    }
    
    const result = await response.json();
    if (result.success && result.formState) {
      console.log('[FormStateService] Found pushed state:', result.formState);
      return { hasPushed: true, formState: result.formState };
    }
    
    return { hasPushed: false, formState: null };
  } catch (err) {
    console.error('[FormStateService] checkForPushedState error', err);
    return { hasPushed: false, formState: null };
  }
};

/**
 * Utility: Flatten an object to rows of path=>value for export
 */
const flattenToRows = (obj, prefix = '') => {
  const rows = [];
  if (obj === null || obj === undefined) return rows;
  if (typeof obj !== 'object') {
    rows.push({ path: prefix || 'value', value: String(obj) });
    return rows;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, idx) => {
      rows.push(...flattenToRows(item, `${prefix}[${idx}]`));
    });
    return rows;
  }

  Object.keys(obj).forEach(key => {
    const val = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (val === null || val === undefined) {
      rows.push({ path, value: '' });
    } else if (typeof val === 'object') {
      rows.push(...flattenToRows(val, path));
    } else {
      rows.push({ path, value: String(val) });
    }
  });
  return rows;
};

/**
 * ADMIN: Export form state as plain-text (JSON) or CSV (flattened path,value)
 */
export const exportFormState = (formState, type = 'txt') => {
  if (!formState) throw new Error('formState required');
  if (type === 'txt') {
    return JSON.stringify(formState, null, 2);
  }

  if (type === 'csv') {
    const rows = flattenToRows(formState);
    const header = 'path,value\n';
    const body = rows.map(r => `"${r.path.replace(/"/g, '""')}","${String(r.value).replace(/"/g, '""')}"`).join('\n');
    return header + body;
  }

  throw new Error('unsupported export type');
};

/**
 * Format timestamp to AEST
 * @param {string} isoString - ISO timestamp
 * @returns {string} Formatted date in AEST
 */
export const formatAEST = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    return isoString;
  }
};
